import { useCallback, useState } from 'react';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH_FOR_COMPLETE } from '@/consts';
import { useStores } from '@/stores';
import { showAlertPopup } from '@/core/presenter/controller/PopupController';
import { INPUT_MSG } from '@/consts/message';

export default function useJoinPwdSettingView(onClick: () => void) {
  const { userStore } = useStores();
  const [checkPassword, setCheckPassword] = useState('');
  // 입력 중에는 에러 메시지를 표시하지 않으므로 항상 true로 유지
  const isPasswordValid = true;
  const isCompleteBtnDisabled = userStore.password !== checkPassword;
  const isCheckPasswordDisabled = (userStore.password?.length ?? 0) < MIN_PASSWORD_LENGTH_FOR_COMPLETE;

  const onChangePassword = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      const truncated = value.slice(0, MAX_PASSWORD_LENGTH);

      userStore.setPassword(truncated);
    },
    [userStore],
  );

  const onChangeCheckPassword = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      if (isCheckPasswordDisabled) {
        e.preventDefault();
        return;
      }

      const value = e.currentTarget.value;
      const truncated = value.slice(0, MAX_PASSWORD_LENGTH);
      setCheckPassword(truncated);
    },
    [isCheckPasswordDisabled],
  );

  const onKeyDownCheckPassword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isCheckPasswordDisabled) {
        e.preventDefault();
      }
    },
    [isCheckPasswordDisabled],
  );

  // 비밀번호 유효성 검사 함수
  const validatePassword = useCallback((password: string | undefined): boolean => {
    if (!password) return false;

    // 8자 이상 20자 이하 검사
    const isLengthValid = password.length >= 8 && password.length <= 20;
    // 영문, 숫자, 특수문자 포함 여부 검사
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return isLengthValid && hasLetter && hasNumber && hasSpecial;
  }, []);

  const onClickCompleteBtn = useCallback(async () => {
    // 비밀번호 유효성 검사
    if (!validatePassword(userStore.password)) {
      await showAlertPopup(INPUT_MSG.INVALID_PASSWORD, true);
      return;
    }

    // 비밀번호 일치 검사
    if (userStore.password !== checkPassword) {
      await showAlertPopup(INPUT_MSG.CHECK_PWD_FAILED, true);
      return;
    }

    userStore.setPassword(checkPassword);
    onClick();
  }, [userStore, checkPassword, onClick, validatePassword]);

  const onBlurPassword = useCallback(() => {
    // 입력 중에는 에러 메시지를 표시하지 않음 (완료 버튼 클릭 시에만 검사)
    // 필요시 여기에 다른 로직 추가 가능
  }, []);

  return {
    password: userStore.password,
    checkPassword,
    isCompleteBtnDisabled,
    isPasswordValid,
    isCheckPasswordDisabled,
    onChangePassword,
    onChangeCheckPassword,
    onClickCompleteBtn,
    onBlurPassword,
    onKeyDownCheckPassword,
  };
}
